import { useEffect, useState } from "react";
import { WarningCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";

const DISMISS_KEY = "fbTokenBannerDismissedOn";

export function TokenExpiryBanner() {
  const api = useApi();
  const { canManageSettings } = useRole();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(sessionStorage.getItem(DISMISS_KEY) === new Date().toDateString());

  useEffect(() => {
    if (!canManageSettings) return;
    api.get("/facebook-direct/status").then((r) => setStatus(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSettings]);

  if (!canManageSettings || dismissed || !status?.connected) return null;
  const invalid = status.token_valid === false;
  const soon = typeof status.days_remaining === "number" && status.days_remaining <= 30;
  if (!invalid && !soon) return null;
  const urgent = invalid || status.days_remaining <= 7;
  const expiryDate = status.expires_at ? new Date(status.expires_at * 1000).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, new Date().toDateString());
    setDismissed(true);
  };

  return (
    <div
      data-testid="fb-token-expiry-banner"
      className={`flex items-center gap-3 px-4 py-2.5 rounded-[12px] mb-4 text-sm ${urgent ? "bg-[#fef2f2] border border-[#fecaca] text-[#b91c1c]" : "bg-[#fff7ed] border border-[#fed7aa] text-[#9a3412]"}`}
    >
      <WarningCircle size={20} weight="fill" className="flex-shrink-0" />
      <p className="flex-1">
        {invalid
          ? "Your Facebook Page connection has stopped working — reconnect it so posts keep going out."
          : `Your Facebook Page access ${expiryDate ? `expires ${expiryDate}` : `expires in ${status.days_remaining} day${status.days_remaining === 1 ? "" : "s"}`} — reconnect before then to avoid interrupted posting.`}
      </p>
      <button
        data-testid="fb-token-banner-reconnect-btn"
        onClick={() => navigate("/settings")}
        className="font-semibold underline hover:opacity-70 whitespace-nowrap"
      >
        Reconnect now
      </button>
      <button data-testid="fb-token-banner-dismiss-btn" onClick={dismiss} className="opacity-60 hover:opacity-100 whitespace-nowrap">
        Dismiss
      </button>
    </div>
  );
}
